// src/helpers/agentLookup.ts
// Tra cứu Mã/Tên đại lý theo AgentId cho các màn của ticket-service.
//
// Lý do tồn tại: Agent nằm ở system-service, còn ticket-service chỉ lưu `AgentId` dạng logical FK
// (README_TICKETHANDLE.md mục 2.1). Vì vậy MỌI DTO của ticket-service (TicketDeliveryDto,
// UnsoldTicketReturnRequestDto, TicketReturnReconciliationDto, TicketReturnReportRowDto) đều chỉ
// trả `agentId`, KHÔNG kèm `agentCode`/`agentName`. FE phải tự map sang tên hiển thị.
//
// Cache-first (TTL 5 phút, in-memory) theo đúng tinh thần `activeTicketTypesCache.ts` — tránh mỗi
// trang lại gọi lại `/system-api/Agent` khi chuyển qua lại giữa các màn.

import { getAgents } from 'helpers/fakebackend_helper';
import { IAgent } from 'slices/agentManagement/types';

export type AgentMap = Record<number, IAgent>;

const CACHE_TTL_MS = 5 * 60 * 1000;
// BE giới hạn PageSize tối đa 100 (GetPaginatedAgentsQuery) — vượt ngưỡng bị ép về 20.
const PAGE_SIZE = 100;

let cachedAgents: IAgent[] | null = null;
let cachedAt = 0;
let inflight: Promise<IAgent[]> | null = null;

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

/**
 * Nạp danh sách đại lý (cache-first). Nhiều component gọi cùng lúc chỉ tạo 1 request nhờ
 * `inflight`.
 */
export const loadAgentsWithCache = (forceReload = false): Promise<IAgent[]> => {
  const isFresh = cachedAgents !== null && Date.now() - cachedAt < CACHE_TTL_MS;
  if (!forceReload && isFresh) return Promise.resolve(cachedAgents as IAgent[]);
  if (!forceReload && inflight) return inflight;

  inflight = getAgents({ PageIndex: 1, PageSize: PAGE_SIZE })
    .then((response) => {
      const items = getPayloadItems(response) as IAgent[];
      cachedAgents = items;
      cachedAt = Date.now();
      return items;
    })
    .catch(() => {
      // Lỗi mạng: trả mảng rỗng để UI vẫn render (hiện "#<id>" thay cho tên), không chặn cả trang
      return [] as IAgent[];
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/** Dựng map tra cứu theo Id để render bảng không phải `find()` trên từng dòng */
export const buildAgentMap = (agents: IAgent[]): AgentMap =>
  agents.reduce<AgentMap>((map, agent) => {
    if (agent.id !== undefined && agent.id !== null) map[Number(agent.id)] = agent;
    return map;
  }, {});

/** Mã đại lý, fallback `#<id>` khi chưa tra được (đại lý mới tạo / ngoài trang cache) */
export const getAgentCode = (map: AgentMap, agentId?: number | null): string => {
  if (agentId === undefined || agentId === null) return '-';
  return map[Number(agentId)]?.code || `#${agentId}`;
};

/** Tên đại lý, fallback `#<id>` khi chưa tra được */
export const getAgentName = (map: AgentMap, agentId?: number | null): string => {
  if (agentId === undefined || agentId === null) return '-';
  return map[Number(agentId)]?.name || `#${agentId}`;
};
